import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requiredField, useForm } from '../../../../../utils/validators';

import { ModerationActions } from '../../../../../models';
import { LANGUAGES } from '../../../../../components/translations/translationModal';

import { Button, WeezHeader, ToastContext, FrameCard } from '@weezevent/nacre';

import LoadingCard from '../../../../../components/loadingCard';
import { TransitionActionSettings } from './handler/SettingsCard';
import TransitionActionComponent from './handler/ActionsHandler';
import { ActionComments } from './handler/Comments';

export default function ActionDetails({ organization, flowId, actionId, modal = false, setExternalDatas = () => {} }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);

    const validators = React.useMemo(() => {
        return {
            name: requiredField,
            type: requiredField
        };
    }, []);

    const [form, setForm, setFormObject, formErrors, formValidate] = useForm(
        {
            action: null,
            action_type: '',
            type: '',
            name: '',
            description: '',
            optional_value: false,
            amount: 1,
            attendee_informations: true,
            keep_form_answers: true,
            keep_membership_code: false,
            destination_status: 'valid',
            to: 'attendee',
            identity_id: '',
            template_id: '',
            destination_event: null,
            destination_rate: null,
            form_field: null,
            allow_replay: false,
            comment_choices: [],
            comment_required: null,
            default_value: '',
            display: true,
            score: null,
            translations: {
                name: {},
                description: {}
            }
        },
        validators
    );

    const [, loadingAction, sync] = ModerationActions.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            id: actionId,
            cache: false,
            allow_cache: false,
            launch: Boolean(actionId),
            onSuccess: resp => {
                const trans = {
                    name: resp.object.translations.name || {},
                    description: resp.object.translations.description || {}
                };
                setFormObject({ ...form, ...resp.object, translations: trans });
            }
        },
        [organization, flowId, actionId]
    );

    const languageOptions = React.useMemo(() => {
        return organization.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization]);

    const defaultLanguage = React.useMemo(() => {
        let defaultLocale = languageOptions.find(it => it.value.includes(organization.default_locale.replace('_', '-').toLowerCase()));
        return defaultLocale?.value || languageOptions[0]?.value;
    }, [organization, languageOptions]);

    const backLink = React.useMemo(() => `/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/actions`, [organization.id, flowId]);

    const handleSubmit = React.useCallback(
        (redirect = false) => {
            let action = new ModerationActions({ ...form });

            if (!Object.keys(action.translations['name'])) delete action.translations['name'];
            if (!Object.keys(action.translations['description'])) delete action.translations['description'];

            return action
                .save({ organizationId: organization.id, flowId })
                .then(() => {
                    toast.success(t('ticket.moderation.statusesTransitions.transition.steps.settings.messageSuccessSave'));
                    if (redirect) {
                        history.push(backLink);
                    } else {
                        sync();
                    }
                })
                .catch(() => {
                    toast.error(t('ticket.moderation.attendee.nexAttendeeError'));
                });
        },
        [form, backLink, organization.id, flowId, sync]
    );

    React.useEffect(() => {
        const isValid = formValidate();
        setExternalDatas({
            errors: !isValid,
            onSave: handleSubmit
        });
    }, [form]);

    return (
        <>
            {!modal && (
                <WeezHeader
                    title={!form.id ? t('ticket.moderation.statusesTransitions.actions.detail.titleAdd') : form?.name}
                    rightComponent={[
                        <Button key={0} primary label={t('ticket.common.action.save')} disabled={!!Object.keys(formErrors).length} onClick={() => handleSubmit(true)} />
                    ]}
                    backLink={{
                        onClick: () => {
                            history.push(backLink);
                        }
                    }}
                />
            )}

            <FrameCard separator title={t('ticket.moderation.statusesTransitions.actions.detail.settingsCardTitle')}>
                <LoadingCard loading={form.id && loadingAction}>
                    <TransitionActionSettings
                        action={form}
                        handleAction={setForm}
                        handleActionObject={setFormObject}
                        languageOptions={languageOptions}
                        defaultLanguage={defaultLanguage}
                    />
                </LoadingCard>
            </FrameCard>

            {form.type && (
                <FrameCard
                    separator
                    title={`${t('ticket.moderation.statusesTransitions.transition.steps.actions.cardTitle')}: ${t(
                        `ticket.moderation.statusesTransitions.transition.steps.actions.${form.type}`
                    )}`}
                >
                    <LoadingCard loading={form.id && loadingAction}>
                        <TransitionActionComponent organizationId={organization.pk} flowId={flowId} action={form} handleAction={setForm} handleActionObject={setFormObject} />
                    </LoadingCard>
                </FrameCard>
            )}

            {form.comment_required !== null && (
                <FrameCard separator title={t('ticket.moderation.statusesTransitions.transition.steps.actions.comments_label')}>
                    <LoadingCard loading={form.id && loadingAction}>
                        <ActionComments action={form} handleAction={setForm} handleSave={handleSubmit} languageOptions={languageOptions} defaultLanguage={defaultLanguage} />
                    </LoadingCard>
                </FrameCard>
            )}
        </>
    );
}
